import { Menu as HLMenu } from '@headlessui/react';
import { MenuItem } from './MenuItem';
import { MenuSpan } from './MenuSpan';
import { MenuButton } from './MenuButton';
import { MenuItems } from './MenuItems';
import { MenuContext } from './useMenuContext';
import tw from 'twin.macro';
import clsx from 'clsx';
import React from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';

export interface Props {
  children: React.ReactNode;
  portal?: boolean;
  large?: boolean;
  fill?: boolean;
  className?: string;
  matchTriggerWidth?: boolean;
  placement?: 'bottom' | 'top' | 'bottom-end' | 'bottom-start';
}

export function Menu(props: Props) {
  const {
    fill = false,
    portal = false,
    children,
    large = false,
    className,
    matchTriggerWidth,
    placement = 'bottom',
  } = props;
  const middleware = [
    offset(8),
    flip(),
    shift({
      padding: 10, // Prevents overflow and scrollbars when menu is too close to the edge (default is 0)
    }),
  ];
  if (matchTriggerWidth) {
    middleware.push(
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    );
  }

  const { refs, floatingStyles } = useFloating({
    middleware,
    placement,
    whileElementsMounted: autoUpdate,
  });

  return (
    <HLMenu as="div" className={clsx('tsh-menu', className)}>
      {({ open }) => (
        <div css={[!fill && tw`inline-block`]}>
          <MenuContext.Provider
            value={{
              fill,
              large,
              open,
              refs,
              floatingStyles,
              portal,
            }}
          >
            {children}
          </MenuContext.Provider>
        </div>
      )}
    </HLMenu>
  );
}

Menu.Button = MenuButton;
Menu.Item = MenuItem;
Menu.Span = MenuSpan;
Menu.Items = MenuItems;
Menu.Divider = MenuItem;

export const SECTION_TITLE_STYLE = tw`text-sm text-neutral-quiet px-4 py-2`;
export const SELECTED_ITEM_STYLE = tw`bg-brand-quiet text-brand-intense font-semibold hover:bg-brand-quiet`;
